import { t } from '@exam-prep/i18n';
import { useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { api } from '../../../lib/api';
import type { AnswerQueue, TimeTracker } from '../model/offline';
import type { Local, OpenQuestion } from '../model/palette';

/** Choosing/marking an answer, and the two heavier steps: next part and submit. */
export function usePlayerActions(args: {
  attemptId: string;
  queue: AnswerQueue;
  timer: TimeTracker;
  managed: boolean;
  load: () => Promise<void>;
  onDone: (managed: boolean) => void;
  setNotice: (n: string | null) => void;
  setError: (e: unknown) => void;
  setLocal: Dispatch<SetStateAction<Record<string, Local>>>;
}) {
  const { attemptId, queue, timer, managed, load, onDone, setNotice, setError, setLocal } = args;
  const [confirm, setConfirm] = useState<'submit' | 'next' | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const transitionRef = useRef(false);

  const answer = (q: OpenQuestion, cur: Local, patch: Partial<Local>) => {
    const next = { ...cur, ...patch };
    setLocal((l) => ({ ...l, [q.itemId]: next }));
    queue.put({
      itemId: q.itemId,
      choice: next.choice,
      marked: next.marked,
      timeMs: timer.total(q.itemId),
    });
  };

  const finish = async (action: 'submit' | 'next') => {
    if (transitionRef.current) return;
    transitionRef.current = true;
    setTransitioning(true);
    try {
      try {
        await queue.flushRequired();
      } catch {
        // Keep the player and durable answers available so the learner can retry safely.
        setNotice(t('miniapp.player.save_failed'));
        return;
      }
      await api('POST', `/app/attempts/${attemptId}/${action}`);
      if (action === 'submit') {
        queue.clear();
        onDone(managed);
      } else {
        setConfirm(null);
        await load();
      }
    } catch (e) {
      // Answers are saved; the server refused the step (paused, deadline passed, already
      // submitted). Show its reason, as before, instead of a misleading connection notice.
      setError(e);
    } finally {
      transitionRef.current = false;
      setTransitioning(false);
    }
  };

  return { confirm, setConfirm, transitioning, answer, finish };
}
